<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title>{{ $t('dashboard.recentActivities') }}</b-card-title>
      <div class="d-flex align-items-center">
        <b-form-select
          v-model="selectedType"
          :options="typeOptions"
          size="sm"
          class="mr-1"
          @change="onTypeChange"
        />
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          size="sm"
          :disabled="isLoading"
          @click="refreshData"
        >
          <feather-icon
            icon="RefreshCwIcon"
            :class="{ 'animate-spin': isLoading }"
          />
        </b-button>
      </div>
    </b-card-header>
    <b-card-body v-if="activities && activities.activities">
      <b-list-group flush>
        <b-list-group-item
          v-for="(activity, index) in activities.activities"
          :key="index"
          class="px-0"
        >
          <div class="d-flex align-items-start">
            <b-avatar
              :variant="`light-${getActivityColor(activity.type)}`"
              size="40"
              class="mr-1"
            >
              <feather-icon
                :icon="getActivityIcon(activity.type)"
                size="20"
              />
            </b-avatar>
            <div class="flex-grow-1">
              <h6 class="mb-0">
                {{ activity.title }}
              </h6>
              <small class="text-muted">{{ activity.description }}</small>
              <div
                class="text-muted mt-50"
                style="font-size: 0.75rem"
              >
                {{ formatDate(activity.timestamp) }}
              </div>
            </div>
            <b-badge :variant="getActivityColor(activity.type)">
              {{ $t(`dashboard.activityType.${activity.type}`) }}
            </b-badge>
          </div>
        </b-list-group-item>
      </b-list-group>

      <!-- Load More -->
      <div
        v-if="activities.total > activities.activities.length"
        class="text-center mt-2"
      >
        <b-button
          variant="outline-primary"
          size="sm"
          @click="loadMore"
        >
          {{ $t('dashboard.loadMore') }}
        </b-button>
      </div>
    </b-card-body>

    <!-- Loading State -->
    <b-card-body v-else-if="isLoading">
      <div class="text-center py-3">
        <b-spinner variant="primary" />
      </div>
    </b-card-body>

    <!-- Empty State -->
    <b-card-body v-else>
      <div class="text-center py-3">
        <feather-icon
          icon="InboxIcon"
          size="48"
          class="text-muted mb-1"
        />
        <p class="text-muted">
          {{ $t('dashboard.noActivities') }}
        </p>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BButton,
  BFormSelect,
  BBadge,
  BSpinner,
  BListGroup,
  BListGroupItem,
  BAvatar,
} from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'

export default {
  name: 'RecentActivitiesWidget',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BButton,
    BFormSelect,
    BBadge,
    BSpinner,
    BListGroup,
    BListGroupItem,
    BAvatar,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      selectedType: 'all',
      currentLimit: 10,
      typeOptions: [
        { value: 'all', text: this.$t('dashboard.activityType.all') },
        { value: 'projects', text: this.$t('dashboard.activityType.projects') },
        { value: 'transactions', text: this.$t('dashboard.activityType.transactions') },
        { value: 'tasks', text: this.$t('dashboard.activityType.tasks') },
      ],
    }
  },
  computed: {
    ...mapGetters('dashboard', ['activities', 'loading', 'filters']),
    isLoading() {
      return this.loading.activities
    },
  },
  methods: {
    ...mapActions('dashboard', ['fetchActivities', 'refreshWidget']),
    onTypeChange() {
      this.currentLimit = 10
      this.fetchActivities({ type: this.selectedType, limit: this.currentLimit })
    },
    refreshData() {
      this.refreshWidget({
        widget: 'activities',
        params: { type: this.selectedType, limit: this.currentLimit },
      })
    },
    loadMore() {
      this.currentLimit += 10
      this.fetchActivities({ type: this.selectedType, limit: this.currentLimit })
    },
    getActivityIcon(type) {
      const icons = {
        project: 'BriefcaseIcon',
        transaction: 'DollarSignIcon',
        task: 'CheckSquareIcon',
      }
      return icons[type] || 'ActivityIcon'
    },
    getActivityColor(type) {
      const colors = {
        project: 'primary',
        transaction: 'success',
        task: 'warning',
      }
      return colors[type] || 'secondary'
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) {
        return this.$t('dashboard.justNow')
      } if (diffMins < 60) {
        return this.$t('dashboard.minutesAgo', { count: diffMins })
      } if (diffHours < 24) {
        return this.$t('dashboard.hoursAgo', { count: diffHours })
      } if (diffDays < 7) {
        return this.$t('dashboard.daysAgo', { count: diffDays })
      }
      return date.toLocaleDateString('ar-EG')
    },
  },
  created() {
    this.selectedType = this.filters.activitiesType || 'all'
    this.currentLimit = this.filters.activitiesLimit || 10
  },
}
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
